import type {OperationInfo, ServerExtensions, ServiceCalls} from "@irati/types/apolloExtensions"
import {ApolloLink, Operation, FetchResult} from "@apollo/client"
import {print} from "graphql/language/printer"

const BATCH_DEBOUNCE_MS = 10
const WARNING = "⛔️"
const ERROR = "❌"
const SUCCESS = "✅"

type Seen = {
  timeoutHandle?: ReturnType<typeof setTimeout>
  ops: Map<
    Operation,
    {
      info: OperationInfo
      result: FetchResult
    }
  >
}

// simple logger for dev and staging to show apollo queries in console. Will
// show batches if available
export const apolloLogger = () => {
  const seenQueries = new Map<string, Seen>()

  return new ApolloLink((operation, forward) => {
    const start = performance.now()

    return forward(operation).map((data) => {
      if (hasLoggingV1(data.extensions)) {
        const reqTime = Math.floor(performance.now() - start)
        const {services, correlationId, operations} = data.extensions.loggingV1
        const key = correlationId
        let seen = seenQueries.get(correlationId)

        if (!seen) {
          seen = {ops: new Map()}
          seenQueries.set(key, seen)
        }

        const handle = setTimeout(log(key, seenQueries, reqTime, services), BATCH_DEBOUNCE_MS)

        // debounce our timer
        clearTimeout(seen.timeoutHandle)
        const operationInfo = operations[operation.operationName]!
        seen.ops.set(operation, {
          info: operationInfo,
          result: data
        })
        // create a new debounce AFTER clearing the old one
        seen.timeoutHandle = handle
      }
      return data
    })
  })
}

function hasLoggingV1(extensions: unknown): extensions is ServerExtensions {
  return (
    extensions && //
    typeof extensions === "object" &&
    extensions !== null &&
    "loggingV1" in extensions &&
    (extensions.loggingV1 as any)
  )
}

function log(key: string, seenQueries: Map<string, Seen>, reqTime: number, services: ServiceCalls) {
  return () => {
    const known = seenQueries.get(key)
    if (!known) {
      console.warn("Impossible! this should have been set when the handle was created")
    } else {
      const output = Array.from(known.ops.entries()).map(
        ([
          {operationName, variables, query},
          {
            result: {data, errors},
            info
          }
        ]) => {
          return {
            operation: operationName,
            variables,
            query: print(query),
            data,
            errors,
            duration: info?.durationMs
          } as const
        }
      )
      const errorCalls = output.filter((o) => o.errors?.length).map((o) => o.operation)
      const successCalls = output.filter((o) => !o.errors?.length).map((o) => o.operation)
      const joinChar = " | "
      const hasError = errorCalls.length > 0
      const hasSuccess = successCalls.length > 0
      const warnings = getWarnings(services)

      const labelInfo = !hasError
        ? successCalls.join(joinChar)
        : [
            hasSuccess && `${SUCCESS} ${successCalls.join(joinChar)}`, //
            `${ERROR} ${errorCalls.join(joinChar)}`
          ]
            .filter(Boolean)
            .join("\n")

      console.groupCollapsed(`🚀 Graphql (${reqTime}ms)${warnings.hasWarnings ? ` ${WARNING}` : ""}:\n${labelInfo}`)

      output.forEach(({query, operation, duration, errors, variables, data}) => {
        const hasErrors = errors?.length ? errors.map((e: any) => e.message || e.errorCode)[0] : false

        // for batches we'll create groups for each request
        if (output.length > 1) {
          console.groupCollapsed(operation + ` [${Math.floor(duration)}ms] ` + (hasErrors ? ERROR : SUCCESS))
        }

        if (hasErrors) {
          console.log("%cErrors", "color: coral; font-weight: 800")
          console.log(errors)
        }

        console.log("%cData", "color: #0096ff; font-weight: 800")
        console.log(data)

        if (Object.keys(variables).length) {
          console.log("%cVariables", "font-weight: 800")
          console.log(variables)
        }

        console.groupCollapsed("%cQuery", "font-weight: 800")
        console.log(query)
        console.groupEnd()

        console.group(`%cService Calls ${warnings.hasWarnings ? WARNING : ""}`, "font-weight: 800")

        if (warnings.hasWarnings) {
          console.warn("These could likely be optimised with DataLoaders!!")
          console.log(warnings.warnings.join("\n"))
        }

        for (const [service, serviceData] of Object.entries(services)) {
          const {_tag} = serviceData.at(0)!
          const multipleServiceCalls = Object.keys(serviceData).length > 1
          if (multipleServiceCalls) {
            console.groupCollapsed(`%c${service} [${_tag}]`, "font-weight: 600")
          }
          // if there are multiple calls, we show the tag in the group, otherwise we'll show them inline
          const serviceType = multipleServiceCalls ? "" : ` [${_tag}] `
          serviceData.forEach((d) => {
            if (d._tag === "ApiData") {
              console.groupCollapsed(
                `${!multipleServiceCalls ? `${service}/` : ""}${d.endpoint} [${Math.floor(d.duration)}ms]${serviceType}`
              )
              console.log(d.url)
              console.log("%cData", "color: #0096ff; font-weight: 500")
              console.log(d.data)
              if (d.query) {
                console.log("%cQuery Params", "font-weight: 500")
                console.log(d.query)
              }
              console.groupEnd()
            } else if (d._tag === "GrpcData") {
              console.groupCollapsed(`${d.service}.${d.method} [${Math.floor(d.duration)}ms]${serviceType}`)
              console.log("%cVariables", "font-weight: 500")
              console.log(d.variables)
              console.log("%cData", "color: #0096ff; font-weight: 500")
              console.log(d.data)
              console.groupEnd()
            } else if (d._tag === "AlgoliaData") {
              console.groupCollapsed(`Algolia [${Math.floor(d.duration)}ms]${serviceType}`)
              console.log("%cFilters", "font-weight: 500")
              console.log(d.filters)
              if (d.query) {
                console.log("%cQuery", "font-weight: 500")
                console.log(d.query)
              }
              console.log("%cOptions", "font-weight: 500")
              console.log(d.options)
              console.log("%cData", "color: #0096ff; font-weight: 500")
              console.log(d.data)
              console.groupEnd()
            }
          })
          if (multipleServiceCalls) {
            console.groupEnd()
          }
        }

        console.groupEnd()

        if (output.length > 1) {
          console.groupEnd()
        }
      })
      console.groupEnd()

      // cleanup
      seenQueries.delete(key)
    }
  }
}

function getWarnings(services: ServiceCalls) {
  const map = new Map<string, Set<any>>()
  const warnings: string[] = []
  Object.entries(services).forEach(([name, calls]) => {
    calls.forEach((info) => {
      if (info._tag === "ApiData") {
        if (!map.has(info.url)) map.set(info.url, new Set())
        const set = map.get(info.url)!
        if (set.has(info.url)) {
          warnings.push(`endpoint called multiple times! ${info.endpoint}`)
        } else {
          set.add(info.url)
        }
      } else if (info._tag === "GrpcData") {
        const api = `${name}.${info.method}`
        if (!map.has(api)) map.set(api, new Set())
        const set = map.get(api)!
        if (set.has(info.variables)) {
          warnings.push(`${api} hit multiple times!`)
        } else {
          set.add(info.variables)
        }
      }
    })
  })

  return {hasWarnings: warnings.length > 0, warnings}
}
